import { useState } from 'react';
import type { FC, ReactElement, ReactNode } from 'react';
import {
    Button,
    Drawer,
    DrawerBody,
    DrawerCloseButton,
    DrawerContent,
    DrawerHeader,
    DrawerOverlay,
    Text,
    useBreakpointValue,
} from '@chakra-ui/react';
import { FormattedMessage } from 'react-intl';
import type { ButtonProps } from '@chakra-ui/react';
import { ContentAsset } from '../content-asset';

const cartButton: ButtonProps = {
    color: 'slt-pepper.1000',
    variant: 'newtext-button-no-hover',
    fontWeight: 400,
};

const checkoutButton: ButtonProps = {
    variant: 'tertiary',
    colorScheme: 'text-link',
    padding: 0,
    fontSize: '14px',
};

export const TermsConditionsPrivacyPolicy: FC<{ isFromCart?: boolean }> = ({
    isFromCart = false,
}) => {
    const [openTC, setOpenTC] = useState(false);
    const [openPP, setOpenPP] = useState(false);

    const prefix = isFromCart ? 'cart' : 'checkout';
    const textStyle = isFromCart ? 'link-small' : 'slt-micro-legend';

    const placement: 'bottom' | 'right' = useBreakpointValue({
        base: 'bottom',
        md: 'right',
    })!;

    const terms = (chunks: ReactNode[]): ReactElement => (
        <Button
            {...(isFromCart ? cartButton : checkoutButton)}
            data-testid={`${prefix}-terms-conditions-button`}
            onClick={(): void => setOpenTC(true)}
        >
            <Text textStyle={textStyle}>{chunks}</Text>
        </Button>
    );

    const policy = (chunks: ReactNode[]): ReactElement => (
        <Button
            {...(isFromCart ? cartButton : checkoutButton)}
            data-testid={`${prefix}-privacy-policy-button`}
            onClick={(): void => setOpenPP(true)}
        >
            <Text textStyle={textStyle}>{chunks}</Text>
        </Button>
    );

    return (
        <div
            data-testid={`${prefix}-terms-policy-text`}
            className="w-full text-center text-sm font-normal"
        >
            <Text
                textStyle={isFromCart ? 'body-small' : 'slt-micro-legend'}
                color={isFromCart ? 'slt-pepper.1000' : undefined}
            >
                <FormattedMessage
                    id="disclaimer.terms_conditions.privacy_policy"
                    defaultMessage="By continuing to check out as a guest, you are agreeing to our <terms>Terms and Conditions</terms> and <policy>Privacy Policy</policy>."
                    values={{
                        terms,
                        policy,
                    }}
                />
            </Text>

            <TermsConditionsDrawer
                placement={placement}
                isOpen={openTC}
                onClose={(): void => setOpenTC(false)}
                prefix={prefix}
            />

            <Drawer
                variant="full"
                placement={placement}
                isOpen={openPP}
                onClose={(): void => setOpenPP(false)}
                returnFocusOnClose={false}
            >
                <DrawerOverlay />
                <DrawerContent>
                    <DrawerCloseButton data-testid={`${prefix}-privacy-policy-close-button`} />
                    <DrawerHeader>
                        <Text as="h3" textStyle="slt-heading-2xl">
                            <FormattedMessage id="privacy_policy" defaultMessage="Privacy Policy" />
                        </Text>
                    </DrawerHeader>
                    <DrawerBody paddingRight={2}>
                        <ContentAsset className="text-xl" contentAssetId="privacy-policy" />
                    </DrawerBody>
                </DrawerContent>
            </Drawer>
        </div>
    );
};

TermsConditionsPrivacyPolicy.defaultProps = {
    isFromCart: false,
};

type TermsConditionsDrawerProps = {
    placement: 'bottom' | 'right';
    isOpen: boolean;
    onClose: () => void;
    prefix: string;
};

export const TermsConditionsDrawer: FC<TermsConditionsDrawerProps> = ({
    placement,
    isOpen,
    onClose,
    prefix,
}) => (
    <Drawer
        variant="full"
        placement={placement}
        isOpen={isOpen}
        onClose={onClose}
        returnFocusOnClose={false}
    >
        <DrawerOverlay />
        <DrawerContent>
            <DrawerCloseButton data-testid={`${prefix}-terms-conditions-close-button`} />
            <DrawerHeader>
                <Text as="h3" textStyle="slt-heading-2xl">
                    <FormattedMessage id="terms_conditions" defaultMessage="Terms & Conditions" />
                </Text>
            </DrawerHeader>
            <DrawerBody paddingRight={2}>
                <ContentAsset className="text-xl" contentAssetId="terms_and_conditions" />
            </DrawerBody>
        </DrawerContent>
    </Drawer>
);
